import reduce from 'lodash/reduce'
import map from 'lodash/map'
import filter from 'lodash/filter'
import countBy from 'lodash/countBy'
import {isAfter} from 'date-fns'
import * as React from 'react'
import {useParams} from 'react-router'
import {fromJust} from '@freckle/maybe'
import useSWR from 'swr'
import {H1} from '@frontrow/console/src/components/typography'
import {isSchoolCrmSynced, parseSchools} from '@frontrow/console/src/entities/models/school'
import {fromOpenApiLicenses, type LicenseT} from '@frontrow/console/src/entities/models/license'
import {SpinnerOverlay} from '@frontrow/console/src/components/spinner'

import {fetchSchoolsByDistrict} from '@frontrow/console/src/helpers/api-schools'
import {fetchSchoolLicenses} from '@frontrow/console/src/helpers/api-schools'
import {firstResponseApiError} from '@frontrow/console/src/entities/models/api-error'
import ErrorAlert from '@frontrow/console/src/components/common/error-alert'
import PanelList from '@frontrow/console/src/components/common/panel-list'
import SchoolLicensesList from '@frontrow/console/src/components/common/school-licenses-list'
import DistrictLicensesSummary from '@frontrow/console/src/components/pages/districts-page/district-licenses/district-licenses-summary'
import {type SchoolLicensesListSchoolProps} from '@frontrow/console/src/components/common/school-licenses-list'
import {useDistrict} from '@frontrow/console/src/hooks/use-districts'
import * as style from './district-licenses.module.scss'

const SCHOOLS_BY_DISTRICT_SWR_KEY = 'schools-by-district'
const SCHOOL_LICENSES_SUMMARY_KEY = 'school-licenses-summary'

type LicensesSummary = {
  numMathLicenses: number
  numElaLicenses: number
  numScienceLicenses: number
  numSocialStudiesLicenses: number
  totalLicenses: number
}

function renderSchoolLicenseRow(
  schoolData: SchoolLicensesListSchoolProps,
  _: number
): React.ReactElement {
  return <SchoolLicensesList key={schoolData.schoolId} {...schoolData} />
}

function getLicensesSummary(licenses: Array<LicenseT>): LicensesSummary {
  const now = new Date()
  const currentLicenses = filter(licenses, license => isAfter(license.expiresAt, now))
  const licensesBySubject = countBy(currentLicenses, license => license.subject)

  return {
    numMathLicenses: licensesBySubject['math'] || 0,
    numElaLicenses: licensesBySubject['ela'] || 0,
    numSocialStudiesLicenses: licensesBySubject['social_studies'] || 0,
    numScienceLicenses: licensesBySubject['science'] || 0,
    totalLicenses: currentLicenses.length
  }
}

function useSchoolsByDistrict(districtId: number) {
  return useSWR([SCHOOLS_BY_DISTRICT_SWR_KEY, districtId], async ([_, id]) => {
    try {
      const response = await fetchSchoolsByDistrict(id)
      return parseSchools(response)
    } catch (err: unknown) {
      const apiError = firstResponseApiError(err)
      throw new Error(apiError.message)
    }
  })
}

function useSchoolLicensesSummary(schoolIds: Array<number>) {
  return useSWR(
    schoolIds.length > 0 ? [SCHOOL_LICENSES_SUMMARY_KEY, schoolIds.join(',')] : null,
    async () => {
      try {
        const licensesPromises = schoolIds.map(schoolId =>
          fetchSchoolLicenses(schoolId)
            .then(response => ({
              schoolId,
              licenses: fromOpenApiLicenses(response)
            }))
            .catch(() => ({
              schoolId,
              licenses: []
            }))
        )

        const allLicenses = await Promise.all(licensesPromises)

        return reduce(
          allLicenses,
          (acc, {schoolId, licenses}) => {
            const licenseSummary = getLicensesSummary(licenses)
            return {...acc, [schoolId]: licenseSummary}
          },
          {} as {[schoolId: number]: LicensesSummary}
        )
      } catch (err: unknown) {
        const apiError = firstResponseApiError(err)
        throw new Error(apiError.message)
      }
    }
  )
}

export function DistrictLicensesContainer(): React.ReactElement {
  const {districtId} = useParams<{districtId: string}>()
  const districtIdNum = parseInt(fromJust(districtId, 'Cannot find districtId in URL'), 10)

  const {
    data: schools,
    error: schoolsError,
    isLoading: isLoadingSchools
  } = useSchoolsByDistrict(districtIdNum)

  const {
    data: district,
    error: districtError,
    isLoading: isLoadingDistrict
  } = useDistrict(districtIdNum)

  const schoolIds = schools ? map(schools, school => school.id) : []

  const {
    data: schoolsLicenseSummary,
    error: licensesError,
    isLoading: isLoadingLicenses
  } = useSchoolLicensesSummary(schoolIds)

  const isLoading = isLoadingSchools || isLoadingDistrict || isLoadingLicenses
  const error = schoolsError?.message || districtError?.message || licensesError?.message

  const title =
    district !== undefined && district !== null
      ? `Editing ${district.name} Licenses`
      : 'No District Information'

  const schoolData = schools
    ? map(schools, school => ({
        schoolId: school.id,
        schoolName: school.name,
        enableModifications: !isSchoolCrmSynced(school)
      }))
    : []

  const sorters = [
    {
      label: 'School name',
      sortMethod: (data: SchoolLicensesListSchoolProps) => data.schoolName
    },
    {
      label: 'Has license',
      sortMethod: (data: SchoolLicensesListSchoolProps) =>
        schoolsLicenseSummary ? -schoolsLicenseSummary[data.schoolId]?.totalLicenses || 0 : 0
    }
  ]

  const numLicenses = schoolsLicenseSummary
    ? reduce(
        map(Object.keys(schoolsLicenseSummary), key => parseInt(key, 10)),
        (acc, key) => {
          return {
            numMathLicenses: acc.numMathLicenses + schoolsLicenseSummary[key].numMathLicenses,
            numElaLicenses: acc.numElaLicenses + schoolsLicenseSummary[key].numElaLicenses,
            numSocialStudiesLicenses:
              acc.numSocialStudiesLicenses + schoolsLicenseSummary[key].numSocialStudiesLicenses,
            numScienceLicenses:
              acc.numScienceLicenses + schoolsLicenseSummary[key].numScienceLicenses
          }
        },
        {
          numMathLicenses: 0,
          numElaLicenses: 0,
          numSocialStudiesLicenses: 0,
          numScienceLicenses: 0
        }
      )
    : {
        numMathLicenses: 0,
        numElaLicenses: 0,
        numSocialStudiesLicenses: 0,
        numScienceLicenses: 0
      }

  const errorNode = error !== null && error !== undefined ? <ErrorAlert>{error}</ErrorAlert> : null

  const body = isLoading ? (
    <SpinnerOverlay />
  ) : (
    <React.Fragment>
      <PanelList
        renderListElement={renderSchoolLicenseRow}
        dataList={schoolData}
        sorters={sorters}
      />
      <DistrictLicensesSummary numSchools={schoolData.length} {...numLicenses} />
    </React.Fragment>
  )

  return (
    <React.Fragment>
      <H1>{title}</H1>
      {errorNode}
      <div className={style.districtLicensesContainer}>{body}</div>
    </React.Fragment>
  )
}
